import { useEffect, useMemo, useRef, useState } from "react"
import type { ReactNode } from "react"
import type { inferRouterOutputs } from "@trpc/server"
import { useNavigate, useParams } from "@tanstack/react-router"
import {
  Activity,
  AlertTriangle,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleDot,
  Clock3,
  Eye,
  GitPullRequest,
  Keyboard,
  MemoryStick,
  MessageSquareText,
  PauseCircle,
  Play,
  Search,
  Settings2,
  Sparkles,
  TerminalSquare,
  Wrench,
  X,
} from "lucide-react"
import { useHotkeys } from "react-hotkeys-hook"
import toast from "react-hot-toast"
import {
  Badge,
  Button,
  cn,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@aix/ui"

import type { AppRouter } from "../../../../aixinternal-api/src/router/_app"
import { trpc } from "../../lib/trpc"
import { RunLogDrawer } from "./RunLogDrawer"

type RouterOutputs = inferRouterOutputs<AppRouter>
type DashboardRow = RouterOutputs["brainAgents"]["dashboard"]["agents"][number]
type BrainRun = DashboardRow["recent_runs"][number]
type BrainTrigger = DashboardRow["triggers"][number]
type LoopMode = "loop" | "manual"
type HealthStatus = "healthy" | "running" | "failing" | "stale" | "disabled" | "no-recent-runs"
type ModeFilter = "all" | LoopMode

const INVESTIGATORY_PROMPT =
  "Investigate current agent health and recent runs. Report findings only. Do not modify files, commit, push, or open a PR."

const PROMPT_GUARDRAILS =
  "Guardrails: investigate and report only. Do not modify files, commit, push, create branches, or open a PR."

const healthStyles: Record<HealthStatus, string> = {
  healthy: "border-emerald-400/30 bg-emerald-400/10 text-emerald-200",
  running: "border-sky-400/30 bg-sky-400/10 text-sky-200",
  failing: "border-rose-400/40 bg-rose-400/10 text-rose-200",
  stale: "border-amber-400/35 bg-amber-400/10 text-amber-200",
  disabled: "border-zinc-400/25 bg-zinc-400/10 text-zinc-300",
  "no-recent-runs": "border-violet-400/30 bg-violet-400/10 text-violet-200",
}

const modeStyles: Record<LoopMode, string> = {
  loop: "border-cyan-300/35 bg-cyan-300/10 text-cyan-100",
  manual: "border-fuchsia-300/35 bg-fuchsia-300/10 text-fuchsia-100",
}

const shortcutRows = [
  ["J / K", "Move agent"],
  ["Shift J / K", "Move run"],
  ["/", "Search"],
  ["N", "Prompt"],
  ["R", "Run QA"],
  ["O", "Open run"],
  ["?", "Shortcuts"],
] as const

function enabledToBoolean(value: number | boolean): boolean {
  return value === true || value === 1
}

function getLoopMode(triggers: BrainTrigger[]): LoopMode {
  const hasLoopTrigger = triggers.some(
    (trigger) => enabledToBoolean(trigger.enabled) && (trigger.type === "cron" || trigger.type === "message"),
  )
  return hasLoopTrigger ? "loop" : "manual"
}

function getHealth(row: DashboardRow): HealthStatus {
  if (!row.health.enabled) return "disabled"
  if (row.health.latest_run_status === "running" || row.health.latest_run_status === "queued") return "running"
  if (row.health.latest_run_status === "failed" || row.health.failure_count > 0) return "failing"
  if (!row.health.last_run_at) return "no-recent-runs"

  const lastRunTime = new Date(row.health.last_run_at).getTime()
  const dayMs = 24 * 60 * 60 * 1000
  if (Number.isFinite(lastRunTime) && Date.now() - lastRunTime > dayMs * 14) return "stale"

  return "healthy"
}

function formatRelativeTime(value?: string | null): string {
  if (!value) return "Never"
  const time = new Date(value).getTime()
  if (!Number.isFinite(time)) return value
  const diffMs = Date.now() - time
  const minute = 60 * 1000
  const hour = 60 * minute
  const day = 24 * hour
  if (diffMs < minute) return "Now"
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`
  return `${Math.floor(diffMs / day)}d ago`
}

function formatDuration(run?: BrainRun | null): string {
  if (!run?.ended_at) return run?.status === "running" ? "Running" : "Open"
  const start = new Date(run.started_at).getTime()
  const end = new Date(run.ended_at).getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end)) return "Done"
  const seconds = Math.max(1, Math.round((end - start) / 1000))
  if (seconds < 60) return `${seconds}s`
  return `${Math.round(seconds / 60)}m`
}

function parseRouteId(value?: string): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function getRunSummary(run?: BrainRun | null): string {
  if (!run) return "No selected run yet."
  if (typeof run.output === "string" && run.output.length > 0) return run.output
  if (run.error) return run.error
  return run.prompt ?? "Run has not produced output yet."
}

function statusIcon(status: HealthStatus) {
  if (status === "healthy") return <CheckCircle2 className="h-3.5 w-3.5" />
  if (status === "failing") return <AlertTriangle className="h-3.5 w-3.5" />
  if (status === "disabled") return <PauseCircle className="h-3.5 w-3.5" />
  if (status === "running") return <CircleDot className="h-3.5 w-3.5 animate-pulse" />
  return <Clock3 className="h-3.5 w-3.5" />
}

function StubBadge() {
  return (
    <span className="rounded-full bg-white/75 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-700">
      Stub
    </span>
  )
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tagName = target.tagName.toLowerCase()
  return tagName === "input" || tagName === "textarea" || tagName === "select" || target.isContentEditable
}

function StatTile({ label, value, sub, icon }: { label: string; value: string; sub: string; icon: ReactNode }) {
  return (
    <div className="min-h-[92px] rounded-lg border border-white/10 bg-white/[0.045] p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">{label}</p>
        <div className="text-zinc-500">{icon}</div>
      </div>
      <p className="mt-2 break-words text-lg font-semibold leading-6 text-zinc-50">{value}</p>
      <p className="mt-1 break-words text-xs leading-5 text-zinc-500">{sub}</p>
    </div>
  )
}

function StubPanel({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200/60 bg-slate-50/90 p-2.5 text-slate-800 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-slate-600">{icon}</span>
          <p className="truncate text-sm font-semibold">{title}</p>
        </div>
        <StubBadge />
      </div>
      <div className="text-sm text-slate-600">{children}</div>
    </div>
  )
}

export default function AgentsCockpitScreen() {
  const utils = trpc.useUtils()
  const navigate = useNavigate()
  const params = useParams({ strict: false }) as { agentId?: string; runId?: string }
  const searchRef = useRef<HTMLInputElement | null>(null)
  const composerRef = useRef<HTMLTextAreaElement | null>(null)
  const [query, setQuery] = useState("")
  const [modeFilter, setModeFilter] = useState<ModeFilter>("all")
  const [healthFilter, setHealthFilter] = useState<"all" | HealthStatus>("all")
  const [runnerFilter, setRunnerFilter] = useState("all")
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null)
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null)
  const [openRunId, setOpenRunId] = useState<number | null>(null)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [prompt, setPrompt] = useState("")
  const routeAgentId = parseRouteId(params.agentId)
  const routeRunId = parseRouteId(params.runId)

  const dashboardQuery = trpc.brainAgents.dashboard.useQuery(undefined, { refetchInterval: 30_000 })
  const runMutation = trpc.brainAgents.run.useMutation({
    onSuccess: async () => {
      toast.success("Investigatory run queued")
      setPrompt("")
      await utils.brainAgents.dashboard.invalidate()
    },
    onError: (error) => toast.error(error.message),
  })

  const rows = useMemo(() => dashboardQuery.data?.agents ?? [], [dashboardQuery.data?.agents])
  const runnerOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.agent.runner).filter(Boolean))).sort(),
    [rows],
  )

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    return rows.filter((row) => {
      const mode = getLoopMode(row.triggers)
      const health = getHealth(row)
      const searchableText = [
        row.agent.name,
        row.agent.repo_path,
        row.agent.description,
        row.agent.runner,
        row.agent.model,
        row.metadata?.owner_handle,
        row.metadata?.cadence_label,
        row.health.next_schedule_label,
        row.health.latest_run_status,
        mode,
        health,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      const matchesQuery = !normalizedQuery || searchableText.includes(normalizedQuery)
      const matchesMode = modeFilter === "all" || mode === modeFilter
      const matchesHealth = healthFilter === "all" || health === healthFilter
      const matchesRunner = runnerFilter === "all" || row.agent.runner === runnerFilter
      return matchesQuery && matchesMode && matchesHealth && matchesRunner
    })
  }, [healthFilter, modeFilter, query, rows, runnerFilter])

  const selectedAgent = useMemo(() => {
    return (
      filteredRows.find((row) => row.agent.id === selectedAgentId) ??
      rows.find((row) => row.agent.id === selectedAgentId) ??
      filteredRows[0] ??
      rows[0] ??
      null
    )
  }, [filteredRows, rows, selectedAgentId])

  const selectedRun = useMemo(() => {
    if (!selectedAgent) return null
    return selectedAgent.recent_runs.find((run) => run.id === selectedRunId) ?? selectedAgent.recent_runs[0] ?? null
  }, [selectedAgent, selectedRunId])

  const selectedMode = selectedAgent ? getLoopMode(selectedAgent.triggers) : "manual"
  const selectedHealth = selectedAgent ? getHealth(selectedAgent) : "no-recent-runs"
  const failedRun = selectedAgent?.health.recent_failed_runs[0] ?? null

  const navigateToRun = (agentId: number, runId?: number | null) => {
    if (runId == null) {
      void navigate({ to: "/agents/cockpit" })
      return
    }
    void navigate({
      to: "/agents/cockpit/$agentId/run/$runId",
      params: { agentId: String(agentId), runId: String(runId) },
    })
  }

  useEffect(() => {
    if (!selectedAgent) return
    if (selectedAgent.agent.id !== selectedAgentId) setSelectedAgentId(selectedAgent.agent.id)
    if (selectedAgent.recent_runs.length > 0 && !selectedAgent.recent_runs.some((run) => run.id === selectedRunId)) {
      setSelectedRunId(selectedAgent.recent_runs[0].id)
    }
  }, [selectedAgent, selectedAgentId, selectedRunId])

  useEffect(() => {
    if (routeAgentId == null) return
    const routeAgent = rows.find((row) => row.agent.id === routeAgentId)
    if (!routeAgent) return
    if (routeAgent.agent.id !== selectedAgentId) setSelectedAgentId(routeAgent.agent.id)
    if (routeRunId != null && routeAgent.recent_runs.some((run) => run.id === routeRunId)) {
      setSelectedRunId(routeRunId)
    }
  }, [routeAgentId, routeRunId, rows, selectedAgentId])

  const moveAgent = (direction: 1 | -1) => {
    if (!selectedAgent || filteredRows.length === 0) return
    const index = filteredRows.findIndex((row) => row.agent.id === selectedAgent.agent.id)
    const next = filteredRows[(index + direction + filteredRows.length) % filteredRows.length]
    setSelectedAgentId(next.agent.id)
    const nextRunId = next.recent_runs[0]?.id ?? null
    setSelectedRunId(nextRunId)
    navigateToRun(next.agent.id, nextRunId)
  }

  const moveRun = (direction: 1 | -1) => {
    if (!selectedAgent || selectedAgent.recent_runs.length === 0) return
    const index = selectedAgent.recent_runs.findIndex((run) => run.id === selectedRun?.id)
    const next =
      selectedAgent.recent_runs[
        (index + direction + selectedAgent.recent_runs.length) % selectedAgent.recent_runs.length
      ]
    setSelectedRunId(next.id)
    navigateToRun(selectedAgent.agent.id, next.id)
  }

  const fireInvestigatoryRun = (customPrompt?: string) => {
    if (!selectedAgent) return
    const trimmedPrompt = customPrompt?.trim()
    runMutation.mutate({
      id: selectedAgent.agent.id,
      prompt: trimmedPrompt ? `${trimmedPrompt}\n\n${PROMPT_GUARDRAILS}` : INVESTIGATORY_PROMPT,
    })
  }

  useHotkeys("j", () => moveAgent(1), { preventDefault: true, enableOnFormTags: false }, [filteredRows, selectedAgent])
  useHotkeys("k", () => moveAgent(-1), { preventDefault: true, enableOnFormTags: false }, [filteredRows, selectedAgent])
  useHotkeys("shift+j", () => moveRun(1), { preventDefault: true, enableOnFormTags: false }, [
    selectedAgent,
    selectedRun,
  ])
  useHotkeys("shift+k", () => moveRun(-1), { preventDefault: true, enableOnFormTags: false }, [
    selectedAgent,
    selectedRun,
  ])
  useHotkeys("/", () => searchRef.current?.focus(), { preventDefault: true, enableOnFormTags: false })
  useHotkeys("n", () => composerRef.current?.focus(), { preventDefault: true, enableOnFormTags: false })
  useHotkeys("r", () => fireInvestigatoryRun(), { preventDefault: true, enableOnFormTags: false }, [selectedAgent])
  useHotkeys(
    "o",
    () => selectedRun && setOpenRunId(selectedRun.id),
    { preventDefault: true, enableOnFormTags: false },
    [selectedRun],
  )
  useHotkeys("?", () => setShowShortcuts((current) => !current), { preventDefault: true, enableOnFormTags: false })
  useHotkeys("shift+/", () => setShowShortcuts((current) => !current), {
    preventDefault: true,
    enableOnFormTags: false,
  })
  useHotkeys("esc", () => {
    setShowShortcuts(false)
    setOpenRunId(null)
  })

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return
      if (event.key !== "?") return
      event.preventDefault()
      setShowShortcuts((current) => !current)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [])

  if (dashboardQuery.isLoading && !dashboardQuery.data) {
    return (
      <div className="min-h-screen bg-zinc-950 p-6 text-zinc-100">
        <div className="mx-auto max-w-7xl rounded-lg border border-white/10 bg-white/[0.04] p-8">
          Loading agent cockpit...
        </div>
      </div>
    )
  }

  if (dashboardQuery.error && !dashboardQuery.data) {
    return (
      <div className="min-h-screen bg-zinc-950 p-6 text-zinc-100">
        <div className="mx-auto max-w-7xl rounded-lg border border-rose-400/30 bg-rose-400/10 p-8">
          {dashboardQuery.error.message}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-12 text-zinc-100 md:pt-0">
      <div className="grid min-h-screen grid-cols-1 xl:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="border-b border-white/10 bg-zinc-950/95 p-3 xl:border-b-0 xl:border-r">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">Agent cockpit</p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-white">Control loop</h1>
            </div>
            <Button
              variant="outline"
              size="iconSm"
              className="border-white/15 bg-white/5"
              onClick={() => setShowShortcuts(true)}
            >
              <Keyboard className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-3 space-y-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <Input
                ref={searchRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search agents"
                className="border-white/10 bg-white/[0.055] pl-9 text-zinc-100 placeholder:text-zinc-500"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Select value={modeFilter} onValueChange={(value) => setModeFilter(value as ModeFilter)}>
                <SelectTrigger className="border-white/10 bg-white/[0.055] text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All modes</SelectItem>
                  <SelectItem value="loop">Loops</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
              <Select value={healthFilter} onValueChange={(value) => setHealthFilter(value as "all" | HealthStatus)}>
                <SelectTrigger className="border-white/10 bg-white/[0.055] text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All health</SelectItem>
                  <SelectItem value="healthy">Healthy</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="failing">Failing</SelectItem>
                  <SelectItem value="stale">Stale</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                  <SelectItem value="no-recent-runs">No runs</SelectItem>
                </SelectContent>
              </Select>
              <Select value={runnerFilter} onValueChange={setRunnerFilter}>
                <SelectTrigger className="border-white/10 bg-white/[0.055] text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All runners</SelectItem>
                  {runnerOptions.map((runner) => (
                    <SelectItem key={runner} value={runner}>
                      {runner}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
            <span>{filteredRows.length} visible</span>
            <span>
              {dashboardQuery.data?.generated_at ? formatRelativeTime(dashboardQuery.data.generated_at) : "Fresh"}
            </span>
          </div>

          <div className="mt-2 max-h-[52vh] space-y-2 overflow-y-auto pr-1 xl:max-h-[calc(100vh-190px)]">
            {filteredRows.map((row) => {
              const health = getHealth(row)
              const mode = getLoopMode(row.triggers)
              const isSelected = selectedAgent?.agent.id === row.agent.id
              return (
                <button
                  key={row.agent.id}
                  type="button"
                  onClick={() => {
                    setSelectedAgentId(row.agent.id)
                    const nextRunId = row.recent_runs[0]?.id ?? null
                    setSelectedRunId(nextRunId)
                    navigateToRun(row.agent.id, nextRunId)
                  }}
                  className={cn(
                    "w-full rounded-lg border p-2.5 text-left transition",
                    isSelected
                      ? "border-cyan-300/45 bg-cyan-300/10"
                      : "border-white/10 bg-white/[0.035] hover:bg-white/[0.06]",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-white">{row.agent.name}</p>
                      <p className="mt-1 truncate text-xs text-zinc-500">
                        {mode} · {row.agent.runner}
                      </p>
                    </div>
                    <Badge className={cn("shrink-0 gap-1 border", healthStyles[health])}>
                      {statusIcon(health)}
                      {health}
                    </Badge>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs text-zinc-500">
                    <span className="truncate">
                      {row.health.next_schedule_label ?? row.metadata?.cadence_label ?? "Manual review"}
                    </span>
                    <span className="shrink-0">{formatRelativeTime(row.health.last_run_at)}</span>
                  </div>
                </button>
              )
            })}
          </div>
        </aside>

        <main className="grid min-h-screen grid-cols-1 gap-3 p-3 xl:grid-cols-[minmax(0,1fr)_280px]">
          <section className="min-w-0 space-y-3">
            <div className="rounded-lg border border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.16),transparent_28%),rgba(255,255,255,0.045)] p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={cn("border", modeStyles[selectedMode])}>
                      {selectedMode === "loop" ? "Loop enabled" : "Manual"}
                    </Badge>
                    <Badge className={cn("gap-1 border", healthStyles[selectedHealth])}>
                      {statusIcon(selectedHealth)}
                      {selectedHealth}
                    </Badge>
                  </div>
                  <h2 className="mt-2 break-words text-3xl font-semibold tracking-tight text-white">
                    {selectedAgent?.agent.name ?? "No agent selected"}
                  </h2>
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-400">
                    {selectedAgent?.agent.description ??
                      "Pick an agent to review its loop, recent runs, tools, memory, and output handoff in one workspace."}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    className="border-white/15 bg-white/5 text-zinc-100"
                    leftIcon={<Settings2 className="h-4 w-4" />}
                  >
                    Edit loop
                  </Button>
                  <Button
                    loading={runMutation.isLoading}
                    leftIcon={<Play className="h-4 w-4" />}
                    onClick={() => fireInvestigatoryRun()}
                  >
                    QA run
                  </Button>
                </div>
              </div>

              <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                <StatTile
                  label="Cadence"
                  value={
                    selectedAgent?.health.next_schedule_label ?? selectedAgent?.metadata?.cadence_label ?? "Manual"
                  }
                  sub={`${selectedAgent?.triggers.length ?? 0} trigger rules`}
                  icon={<Activity className="h-4 w-4" />}
                />
                <StatTile
                  label="Latest"
                  value={selectedRun ? selectedRun.status : "No run"}
                  sub={
                    selectedRun
                      ? `${formatRelativeTime(selectedRun.started_at)} · ${formatDuration(selectedRun)}`
                      : "Awaiting first signal"
                  }
                  icon={<CircleDot className="h-4 w-4" />}
                />
                <StatTile
                  label="Review"
                  value={
                    selectedAgent?.metadata?.review_sla_hours
                      ? `${selectedAgent.metadata.review_sla_hours}h SLA`
                      : "Human check"
                  }
                  sub={selectedAgent?.metadata?.owner_handle ?? "No owner set"}
                  icon={<Eye className="h-4 w-4" />}
                />
                <StatTile
                  label="Risk"
                  value={selectedMode === "loop" ? "PR gated" : "Report-only"}
                  sub={`${selectedAgent?.agent.base_branch ?? "develop"} · ${selectedAgent?.agent.model ?? "default model"}`}
                  icon={<GitPullRequest className="h-4 w-4" />}
                />
              </div>
            </div>

            <div className="rounded-lg border border-white/10 bg-white/[0.035] p-2.5">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Run stack</p>
                  <p className="mt-1 text-sm text-zinc-400">Select a recent run or start a report-only QA pass.</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="iconSm" className="text-zinc-300" onClick={() => moveRun(-1)}>
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="iconSm" className="text-zinc-300" onClick={() => moveRun(1)}>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                {(selectedAgent?.recent_runs ?? []).slice(0, 10).map((run) => (
                  <button
                    key={run.id}
                    type="button"
                    onClick={() => {
                      setSelectedRunId(run.id)
                      selectedAgent && navigateToRun(selectedAgent.agent.id, run.id)
                    }}
                    className={cn(
                      "min-w-[170px] rounded-lg border px-3 py-2 text-left",
                      selectedRun?.id === run.id
                        ? "border-cyan-300/45 bg-cyan-300/10"
                        : "border-white/10 bg-white/[0.04]",
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-white">Run #{run.id}</span>
                      <span className="text-xs text-zinc-500">{formatDuration(run)}</span>
                    </div>
                    <p className="mt-1 truncate text-xs text-zinc-500">
                      {run.status} · {formatRelativeTime(run.started_at)}
                    </p>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => fireInvestigatoryRun()}
                  className="min-w-[170px] rounded-lg border border-dashed border-white/20 bg-white/[0.025] px-3 py-2 text-left text-sm text-zinc-300"
                >
                  <span className="flex items-center gap-2">
                    <Play className="h-4 w-4" />
                    New QA run
                  </span>
                  <span className="mt-1 block text-xs text-zinc-500">Investigatory only</span>
                </button>
              </div>
            </div>

            <section className="rounded-lg border border-white/10 bg-zinc-900/80">
              <div className="flex items-center justify-between gap-3 border-b border-white/10 p-3">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Workspace</p>
                  <h3 className="mt-1 truncate text-lg font-semibold text-white">
                    {selectedRun ? `Run #${selectedRun.id}` : "No run selected"}
                  </h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/15 bg-white/5 text-zinc-100"
                  disabled={!selectedRun}
                  onClick={() => selectedRun && setOpenRunId(selectedRun.id)}
                  leftIcon={<Eye className="h-4 w-4" />}
                >
                  Open
                </Button>
              </div>
              <div className="max-h-[620px] min-h-[460px] overflow-y-auto p-3">
                <div className="rounded-lg border border-cyan-300/15 bg-cyan-300/10 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-cyan-100">
                    <MessageSquareText className="h-4 w-4" />
                    Prompt
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-zinc-300">
                    {selectedRun?.prompt ?? INVESTIGATORY_PROMPT}
                  </p>
                </div>
                <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.035] p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-white">
                    <Sparkles className="h-4 w-4 text-amber-200" />
                    Agent output
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-zinc-300">
                    {getRunSummary(selectedRun)}
                  </p>
                </div>
              </div>
              <form
                className="border-t border-white/10 p-3"
                onSubmit={(event) => {
                  event.preventDefault()
                  fireInvestigatoryRun(prompt)
                }}
              >
                <Textarea
                  ref={composerRef}
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  placeholder="Ask this agent to investigate something. It will be forced to report-only mode."
                  className="min-h-[80px] border-white/10 bg-white/[0.055] text-zinc-100 placeholder:text-zinc-500"
                />
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-zinc-500">
                    QA launches are guarded: investigate only, no files, commits, branches, or PRs.
                  </p>
                  <Button
                    type="submit"
                    loading={runMutation.isLoading}
                    disabled={!selectedAgent}
                    leftIcon={<Play className="h-4 w-4" />}
                  >
                    Send QA run
                  </Button>
                </div>
              </form>
            </section>
          </section>

          <aside className="space-y-2.5">
            <StubPanel icon={<Wrench className="h-4 w-4" />} title="Tools">
              <div className="space-y-1.5">
                <p className="flex items-center gap-2 break-all">
                  <TerminalSquare className="h-4 w-4 shrink-0" />
                  repo shell: {selectedAgent?.agent.repo_path ?? "not selected"}
                </p>
                <p>Debug command routing and skill permissions will live here.</p>
              </div>
            </StubPanel>
            <StubPanel icon={<MemoryStick className="h-4 w-4" />} title="Learning memory">
              <p>{selectedAgent?.metadata?.notes ?? "Pinned facts, failure lessons, and operating preferences."}</p>
            </StubPanel>
            <StubPanel icon={<GitPullRequest className="h-4 w-4" />} title="PR draft">
              <p>{selectedRun?.pr_url ?? "PR summaries, branch diffs, and approval state will appear here."}</p>
            </StubPanel>
            <StubPanel icon={<Brain className="h-4 w-4" />} title="Learned">
              <p>Persisted lesson suggestions from this run, linked back to agent memory after human approval.</p>
            </StubPanel>
            <StubPanel icon={<Activity className="h-4 w-4" />} title="Watch">
              <p>
                {failedRun
                  ? `Last failure: run #${failedRun.id} ${formatRelativeTime(failedRun.started_at)}.`
                  : "Health checks, alert thresholds, and review reminders."}
              </p>
            </StubPanel>
            <div className="rounded-lg border border-white/10 bg-white/[0.035] p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-white">Keyboard</p>
                <Button
                  variant="ghost"
                  size="iconXs"
                  className="text-zinc-400"
                  onClick={() => setShowShortcuts((current) => !current)}
                >
                  <Keyboard className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="mt-3 space-y-2">
                {shortcutRows.map(([keys, label]) => (
                  <div key={keys} className="flex items-center justify-between gap-3 text-xs">
                    <span className="text-zinc-500">{label}</span>
                    <kbd className="rounded border border-white/10 bg-white/10 px-2 py-1 font-mono text-zinc-200">
                      {keys}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </main>
      </div>

      {showShortcuts && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowShortcuts(false)}
        >
          <div
            className="w-full max-w-md rounded-lg border border-white/10 bg-zinc-950 p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Cockpit shortcuts</h3>
              <Button variant="ghost" size="iconSm" className="text-zinc-400" onClick={() => setShowShortcuts(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-4 space-y-2">
              {shortcutRows.map(([keys, label]) => (
                <div
                  key={keys}
                  className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.035] p-3"
                >
                  <span className="text-sm text-zinc-300">{label}</span>
                  <kbd className="rounded border border-white/10 bg-white/10 px-2 py-1 font-mono text-sm text-zinc-100">
                    {keys}
                  </kbd>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {openRunId != null && <RunLogDrawer runId={openRunId} onClose={() => setOpenRunId(null)} />}
    </div>
  )
}
